import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { withAuthenticationControllerWrapper } from "../../../pages/authentication/AuthenticationControllerWrapper";
import ProfilePictureController from "../Cards/user-card-controller/ProfilePictureController";
import { UserProfileService } from "../../../pages/user-profile/service/userProfileService";
import {
  RefrenceEntity,
  RefrenceType,
} from "../../../pages/user-profile/models/UserProfile";
import useLoggedUser from "../../../hooks/useLoggedUser";
import IconHome from "../../../styles/icons/IconHome";
import useUserRole from "../../../hooks/useUserRole";
import { UserRole } from "../../../utils";
import { useOrganisationStatus } from "../../../context/organisationStatus";

const ProfileMenuController = ({ ...hocProps }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<string>("");

  const menuRef = useRef<HTMLDivElement | null>(null);

  const loggedUser = useLoggedUser();
  const userId = loggedUser?.userId || "";
  const userProfileService = new UserProfileService();

  const firstName = hocProps.userName?.split(" ")[0] || "";
  const lastName = hocProps.userName?.split(" ")[1] || "";

  const toggle = () => setIsOpen((prev) => !prev);

  const currentUserRole = useUserRole();
  const isGlobalAdmin = currentUserRole === UserRole.GlobalAdmin;
  const { isOrganisationActive } = useOrganisationStatus();
  const canSeeWorkspace =
    isOrganisationActive &&
    (isGlobalAdmin || currentUserRole === UserRole.Administrator);

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        if (!loggedUser?.userId) return;

        const payload = {
          Reference: RefrenceEntity.Users,
          Type: RefrenceType.ProfilePicture,
          ReferenceId: Number(userId),
        };

        const data = await userProfileService.fetchUserProfilePicture(payload);

        if (data?.succeeded) {
          const base64Data = data.data.content;
          const contentType = data.data.contentType ?? "image/png";
          const imageUrl = `data:${contentType};base64,${base64Data}`;
          setProfileImage(imageUrl);
        }
      } catch (err: any) {
        if (err?.message?.includes("404")) {
          setProfileImage("");
        } else {
          console.error("Error fetching profile picture:", err);
        }
      }
    };

    fetchProfileImage();
  }, [loggedUser?.userId, userId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="profile-section">
      <div className="profile-name">
        <div className="profile-name-text">{hocProps.userName}</div>
        <div className="profile-name-Auth">
          {hocProps.userRole === UserRole.GlobalAdmin
            ? "Global Admin"
            : hocProps.userRole}
        </div>
      </div>

      <div className="profile-menu-wrapper" ref={menuRef}>
        <div className="profile-picture" onClick={toggle}>
          {profileImage ? (
            <img src={profileImage} alt="profile" />
          ) : (
            <ProfilePictureController
              firstName={firstName}
              lastName={lastName}
              image={profileImage}
            />
          )}
        </div>

        {isOpen && (
          <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
            {/* <Link
              to="/update-password"
              className="list-styles"
              onClick={() => setIsOpen(false)}
            >
              <div className="menu-item">
                <div className="menu-item-name">Update Password</div>
              </div>
            </Link> */}
            {canSeeWorkspace && (
              <>
                <div className="section-title">
                  <IconHome breadcrumb="workSpace" activeColor="#0f1560" />
                  Workspace
                </div>

                {isGlobalAdmin && (
                  <Link
                    to="/organisations"
                    className="list-styles"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="menu-item">
                      <IconHome breadcrumb="companyIcon" activeColor="#9aa3b2" />
                      <div className="menu-item-name">Organisations</div>
                    </div>
                  </Link>
                )}

                <Link
                  to="/customers"
                  className="list-styles"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="menu-item">
                    <IconHome breadcrumb="customers" activeColor="#9aa3b2" />
                    <div className="menu-item-name">Customers</div>
                  </div>
                </Link>

                <Link
                  to="/jobs"
                  className="list-styles"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="menu-item">
                    <IconHome breadcrumb="jobs" activeColor="#9aa3b2" />
                    <div className="menu-item-name">Jobs</div>
                  </div>
                </Link>

                <Link
                  to="/user/users"
                  className="list-styles"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="menu-item">
                    <IconHome
                      breadcrumb="userManagement"
                      activeColor="#9aa3b2"
                    />
                    <div className="menu-item-name">Manage Users</div>
                  </div>
                </Link>

                {/* <Link
                  to="/user/requests"
                  className="list-styles"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="menu-item">
                    <IconHome
                      breadcrumb="userManagement"
                      activeColor="#9aa3b2"
                    />
                    <div className="menu-item-name">Manage Requests</div>
                  </div>
                </Link> */}
              </>
            )}

            <div className={`section-title settings-title ${canSeeWorkspace ? "mt-3" : ""}`}>
              <IconHome breadcrumb="settings" activeColor="#0f1560" />
              Settings
            </div>

            <Link
              to="/user-profile"
              className="list-styles"
              onClick={() => setIsOpen(false)}
            >
              <div className="menu-item">
                <IconHome breadcrumb="profilePicture" activeColor="#9aa3b2" />
                <div className="menu-item-name">User Profile</div>
              </div>
            </Link>

            <div className="profile-icons">
              <Link
                to={"/"}
                className="list-styles"
                onClick={hocProps.handleLogout}
              >
                <div className="menu-item">
                  <IconHome breadcrumb="logout" activeColor="#9aa3b2" />
                  <div className="menu-item-name">Log out</div>
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthenticationControllerWrapper(ProfileMenuController);
